import { Globe, Search, ShoppingCart, Zap, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import ConsultationModal from '@/components/ConsultationModal';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const WebSEO = () => {
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const features = [
    {
      icon: Globe,
      title: t('webSeo.modernWebDesign'),
      description: t('webSeo.modernWebDesignDescription')
    },
    {
      icon: Search,
      title: t('webSeo.seoOptimization'), 
      description: t('webSeo.seoOptimizationDescription')
    },
    {
      icon: ShoppingCart,
      title: t('webSeo.ecommerceSolutions'),
      description: t('webSeo.ecommerceSolutionsDescription')
    },
    {
      icon: Zap,
      title: t('webSeo.performanceOptimization'),
      description: t('webSeo.performanceOptimizationDescription')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Back Button */}
      <section className="pt-24 pb-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link to="/">
            <Button variant="outline-light" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('jobs.backToHome')}
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Hero Section */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center animate-fade-in">
            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-accent to-secondary mb-8 glow-accent">
              <Globe className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
              {t('webSeo.title')}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              {t('webSeo.heroDescription')}
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
              onClick={() => setIsConsultationOpen(true)}
            >
              {t('webSeo.launchWebsite')}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('webSeo.whatWeCreate')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-6 sm:p-8 rounded-3xl card-gradient border border-border/50 hover:border-primary/50 transition-smooth hover:scale-105 hover:glow-primary animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-accent to-secondary glow-accent mb-6">
                    <feature.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-display font-bold text-center mb-16">
            {t('webSeo.ourWebProcess')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: t('webSeo.strategyPlanning'), description: t('webSeo.strategyPlanningDescription') },
              { step: "02", title: t('webSeo.designDevelopment'), description: t('webSeo.designDevelopmentDescription') },
              { step: "03", title: t('webSeo.seoImplementation'), description: t('webSeo.seoImplementationDescription') },
              { step: "04", title: t('webSeo.launchGrowth'), description: t('webSeo.launchGrowthDescription') }
            ].map((item, index) => (
              <div 
                key={item.step}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="text-4xl font-display font-bold gradient-text mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-display font-bold mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-display font-bold mb-6">
            {t('webSeo.readyToDominate')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            {t('webSeo.readyToDominateDescription')}
          </p>
          <Button 
            size="lg" 
            className="gradient-primary text-lg px-8 py-6 rounded-full glow-primary transition-spring hover:scale-105"
            onClick={() => setIsConsultationOpen(true)}
          >
            {t('webSeo.startWebProject')}
          </Button>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </div>
  );
};

export default WebSEO;